import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { API_BASE } from '../config/api';
import { supabase } from '../config/supabase';

const AuthContext = createContext();

/**
 * Centralized error handler for Supabase Auth errors.
 * Addresses the "body stream already read" bug and translates various error patterns
 * into user-friendly messages.
 */
const translateAuthError = (error, context = 'login') => {
  if (!error) return null;

  const msg = (error.message || '').toLowerCase();
  const status = error.status;

  // Known Supabase JS bug: "body stream already read" or JSON parsing errors
  // often occur when auth returns 400 (invalid credentials) or during network issues
  if (
    msg.includes('body stream already read') ||
    msg.includes('body stream') ||
    (msg.includes('json') && msg.includes('parse')) ||
    (msg.includes('unexpected token') && msg.includes('json'))
  ) {
    if (context === 'login') return 'Invalid email or password.';
    if (context === 'register') return 'Registration failed. Please check your details and try again.';
    return 'Authentication failed. Please try again.';
  }

  // Network/Connection errors
  if (msg.includes('failed to fetch') || msg.includes('network') || msg.includes('load failed')) {
    return 'Unable to connect. Please check your internet connection and try again.';
  }

  // Rate limiting
  if (msg.includes('too many requests') || msg.includes('rate limit') || status === 429) {
    return 'Too many attempts. Please wait a moment and try again.';
  }

  if (context === 'login') {
    if (msg.includes('invalid login credentials') || msg.includes('invalid_credentials') || msg.includes('invalid email or password')) {
      return 'Invalid email or password.';
    }
    if (msg.includes('email not confirmed')) {
      return 'Your email is not confirmed. Please check your inbox for a confirmation link.';
    }
    if (msg.includes('email logins are disabled')) {
      return 'Email login is currently disabled. Please try another login method.';
    }
    if (msg.includes('user banned') || msg.includes('banned')) {
      return 'This account has been suspended. Please contact support.';
    }
    if (status === 400) {
      return 'Invalid login details. Please check your email and password.';
    }
    return error.message || 'Login failed. Please try again.';
  }

  if (context === 'register') {
    if (msg.includes('already registered') || msg.includes('already exists') || msg.includes('duplicate')) {
      return 'An account with this email already exists. Please sign in instead.';
    }
    if (msg.includes('password') && (msg.includes('weak') || msg.includes('short') || msg.includes('least'))) {
      return 'Password is too weak. Please use at least 6 characters.';
    }
    if (msg.includes('valid email') || msg.includes('invalid email')) {
      return 'Please enter a valid email address.';
    }
    if (msg.includes('signups not allowed') || msg.includes('signup disabled')) {
      return 'New registrations are currently disabled. Please try again later.';
    }
    if (status === 400) {
      return 'Registration failed. Please check your details and try again.';
    }
    return error.message || 'Registration failed. Please try again.';
  }

  return error.message || 'Authentication failed. Please try again.';
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const initialized = useRef(false);
  const authActionInProgress = useRef(false);

  // Fetch user profile from backend.
  // We always merge in `app_metadata` from the Supabase session — that's where
  // role lives (server-managed, used by Phase-3 admin gating). Backend
  // /auth/me does NOT echo app_metadata back, so we pin it on client-side.
  const fetchUserProfile = useCallback(async (accessToken, supabaseUser) => {
    const appMetadata = supabaseUser?.app_metadata || {};
    try {
      const response = await axios.get(`${API_BASE}/auth/me`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      return { ...response.data, app_metadata: appMetadata };
    } catch (error) {
      console.warn('Could not fetch profile from backend, using Supabase user data:', error.message);
      // Return minimal user from Supabase
      return {
        id: supabaseUser.id,
        email: supabaseUser.email,
        name: supabaseUser.user_metadata?.name || supabaseUser.email?.split('@')[0] || 'User',
        account_type: supabaseUser.user_metadata?.account_type || 'individual',
        app_metadata: appMetadata,
      };
    }
  }, []);

  const safeSetUser = useCallback((newUser) => {
    setUser(prevUser => {
      // Deep comparison to avoid unnecessary re-renders
      if (JSON.stringify(prevUser) === JSON.stringify(newUser)) return prevUser;
      return newUser;
    });
  }, []);

  useEffect(() => {
    let mounted = true;

    // Initialize auth from Supabase session
    const initAuth = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Error getting session:', error);
          if (mounted) setLoading(false);
          return;
        }
        
        if (session && mounted) {
          setToken(session.access_token);
          const profile = await fetchUserProfile(session.access_token, session.user);
          if (mounted) {
            safeSetUser(profile);
            initialized.current = true;
          }
        }
      } catch (error) {
        console.error('Auth initialization failed:', error);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    initAuth();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return;

      // Skip INITIAL_SESSION entirely — initAuth() handles initial session
      if (event === 'INITIAL_SESSION') {
        return;
      }

      if (event === 'SIGNED_IN' && session) {
        // Skip if login() or register() already handled this sign-in
        if (authActionInProgress.current) {
          setToken(session.access_token);
          return;
        }
        setToken(session.access_token);
        const profile = await fetchUserProfile(session.access_token, session.user);
        safeSetUser(profile);
        setLoading(false);
        initialized.current = true;
      } else if (event === 'SIGNED_OUT') {
        setToken(null);
        safeSetUser(null);
        setLoading(false);
        initialized.current = true;
      } else if (event === 'TOKEN_REFRESHED' && session) {
        setToken(session.access_token);
      }
    });

    return () => {
      mounted = false;
      subscription?.unsubscribe();
    };
  }, [fetchUserProfile, safeSetUser]);

  const register = useCallback(async (email, password, name, account_type = 'individual') => {
    // Client-side validation
    const trimmedEmail = (email || '').trim().toLowerCase();
    const trimmedName = (name || '').trim();
    if (!trimmedName) {
      throw new Error('Please enter your name');
    }
    if (!trimmedEmail) {
      throw new Error('Please enter your email address');
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      throw new Error('Please enter a valid email address');
    }
    if (!password || password.length < 6) {
      throw new Error('Password must be at least 6 characters long');
    }

    let signUpResult = null;
    try {
      signUpResult = await supabase.auth.signUp({
        email: trimmedEmail,
        password,
        options: {
          data: {
            name: trimmedName,
            account_type
          }
        }
      });
    } catch (networkError) {
      throw new Error(translateAuthError(networkError, 'register'));
    }

    const { data, error } = signUpResult || {};

    if (error) {
      throw new Error(translateAuthError(error, 'register'));
    }

    if (!data?.user) {
      throw new Error('Registration failed — no user was returned. Please try again.');
    }

    // Check if user already exists (Supabase returns user with identities=[] for duplicate signups)
    if (data.user.identities && data.user.identities.length === 0) {
      throw new Error('An account with this email already exists. Please sign in instead.');
    }

    // If we have a session (email confirmation disabled), set it
    if (data.session) {
      authActionInProgress.current = true;
      setToken(data.session.access_token);
      
      // Ensure profile exists in backend by calling register endpoint
      try {
        const response = await axios.post(`${API_BASE}/auth/register`, 
          { email: trimmedEmail, password, name: trimmedName, account_type },
          { headers: { Authorization: `Bearer ${data.session.access_token}` }}
        );
        safeSetUser(response.data.user);
        initialized.current = true;
        setTimeout(() => { authActionInProgress.current = false; }, 500);
        return { user: response.data.user, access_token: data.session.access_token };
      } catch (backendError) {
        // Profile might already exist, try to get it
        const profile = await fetchUserProfile(data.session.access_token, data.user);
        safeSetUser(profile);
        initialized.current = true;
        setTimeout(() => { authActionInProgress.current = false; }, 500);
        return { user: profile, access_token: data.session.access_token };
      }
    } else {
      // Email confirmation is required - user needs to check email
      return { 
        user: { 
          id: data.user.id, 
          email: data.user.email, 
          name: trimmedName 
        }, 
        needsEmailConfirmation: true 
      };
    }
  }, [fetchUserProfile, safeSetUser]);

  const login = useCallback(async (email, password) => {
    // Client-side validation
    const trimmedEmail = (email || '').trim().toLowerCase();
    if (!trimmedEmail) {
      throw new Error('Please enter your email address');
    }
    if (!password) {
      throw new Error('Please enter your password');
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      throw new Error('Please enter a valid email address');
    }

    let result = null;

    try {
      result = await supabase.auth.signInWithPassword({
        email: trimmedEmail,
        password
      });
    } catch (networkError) {
      throw new Error(translateAuthError(networkError, 'login'));
    }

    // Handle Supabase response errors (returned in { data, error } pattern)
    const { data, error } = result || {};

    if (error) {
      throw new Error(translateAuthError(error, 'login'));
    }

    if (!data?.session) {
      throw new Error('Login failed — no session was returned. Please try again.');
    }

    setToken(data.session.access_token);
    
    authActionInProgress.current = true;
    const profile = await fetchUserProfile(data.session.access_token, data.user);
    safeSetUser(profile);
    initialized.current = true;
    
    // Allow onAuthStateChange SIGNED_IN event to be caught and skipped
    setTimeout(() => { authActionInProgress.current = false; }, 500);
    
    return { user: profile, access_token: data.session.access_token };
  }, [fetchUserProfile, safeSetUser]);

  const signInWithOAuth = useCallback(async (provider = 'google') => {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: window.location.origin,
        },
      });

      if (error) {
        throw new Error(translateAuthError(error, 'login'));
      }

      return data;
    } catch (networkError) {
      throw new Error(translateAuthError(networkError, 'login'));
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('Supabase signOut error:', error);
      }
    } catch (error) {
      console.error('Logout error:', error);
    }
    setToken(null);
    setUser(null);
  }, []);

  const updateUser = useCallback((updatedUserData) => {
    setUser(prev => ({ ...prev, ...updatedUserData }));
  }, []);

  const value = useMemo(() => ({
    user, token, loading, register, login, logout, updateUser, signInWithOAuth
  }), [user, token, loading, register, login, logout, updateUser, signInWithOAuth]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * useIsAdmin — true iff the current user has Supabase
 * `app_metadata.role === 'admin'`. Returns `{ isAdmin, loading }` so callers
 * can avoid the false-flash when the auth is still bootstrapping.
 *
 * The role lives in `app_metadata` (server-managed, can't be tampered from
 * the client). We never trust this for back-end access decisions — the
 * backend re-validates via `services/admin_auth.get_current_admin_user`.
 * Here we use it strictly to decide which UI surfaces to render.
 */
export const useIsAdmin = () => {
  const { user, loading } = useAuth();
  const isAdmin = Boolean(user?.app_metadata?.role === 'admin');
  return { isAdmin, loading };
};

export const getAuthHeaders = () => {
  // For synchronous usage, check localStorage for the Supabase token
  try {
    // Supabase stores session in localStorage with a key based on the project URL
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
    const projectRef = supabaseUrl.split('//')[1]?.split('.')[0] || '';
    const storageKey = `sb-${projectRef}-auth-token`;
    
    const storedData = localStorage.getItem(storageKey);
    if (storedData) {
      const parsed = JSON.parse(storedData);
      if (parsed?.access_token) {
        return { Authorization: `Bearer ${parsed.access_token}` };
      }
    }
  } catch (e) {
    console.error('Error getting auth headers:', e);
  }
  
  return {};
};

// Async version for when you need to ensure fresh token
export const getAuthHeadersAsync = async () => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) {
      return { Authorization: `Bearer ${session.access_token}` };
    }
  } catch (e) {
    console.error('Error getting auth headers async:', e);
  }
  return {};
};
